import _defineProperty from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import VideoOverlay from "@/components/Player/VideoOverlay/index";
import { useDeviceInfoQuery } from "@/hooks";
import { plalaQualityLevels, streaksQualityLevels } from "@/lib/constants";
import debugLog from "@/lib/debugLog";
import { MEDIA_ERROR_MESSAGES, PLAYER_ERROR_MESSAGES, StreaksPlayerError, WATCH_ERROR_MESSAGES } from "@/lib/errors";
import isShowValuePlanLabel from "@/lib/isShowValuePlanLabel";
import maybeIOS from "@/lib/maybeIOS";
import { liveStatus } from "@/lib/metaUtil";
import styles from "@/styles/Video.module.scss";
import classnames from "classnames";
import Image from "next/image";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import attachNPAWPlugin from "./NPAWPlugin";
import PlayerSurface from "./PlayerSurface";
import FullScreenOverlay from "./VideoOverlay/FullScreenOverlay";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

function autoPlayEnabled(deviceInfo, disablePause) {
  debugLog({
    maybeIOS: maybeIOS(deviceInfo)
  });
  debugLog({
    ua: deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.ua,
    isIOS: deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.isIOS,
    isIPad: deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.isIPad,
    isSafari: deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.isSafari,
    isMac: deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.isMac
  }); // iOSではautoPlayしない

  if (maybeIOS(deviceInfo)) return false; // pauseイベントの中でplayをコールするという力技を行うとSafariのAuto-Play-Blockingに引っかかった時にエラーが無限ループするのでsafariで後追いライブだったらオートプレイをしない

  if (deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isSafari && disablePause) return false;
  return true;
}

function StreaksPlayer(_ref) {
  var _watch$episode, _watch$episode$channe, _classnames;

  var _ref$playerOption = _ref.playerOption,
      playerOption = _ref$playerOption === void 0 ? {} : _ref$playerOption,
      poster = _ref.poster,
      watch = _ref.watch,
      watchState = _ref.watchState,
      entitlements = _ref.entitlements,
      onReady = _ref.onReady,
      user = _ref.user;

  var _useDeviceInfoQuery = useDeviceInfoQuery({
    fetchPolicy: "cache-first"
  }),
      _useDeviceInfoQuery$d = _useDeviceInfoQuery.data;

  _useDeviceInfoQuery$d = _useDeviceInfoQuery$d === void 0 ? {} : _useDeviceInfoQuery$d;
  var deviceInfo = _useDeviceInfoQuery$d.deviceInfo;
  var router = useRouter();
  var playerRef = useRef();
  var overlayRef = useRef(null);

  var _useState = useState(),
      playerError = _useState[0],
      setPlayerError = _useState[1];

  var _useState2 = useState(false),
      mediaLoaded = _useState2[0],
      setMediaLoaded = _useState2[1];

  var _useState3 = useState(false),
      isFullScreen = _useState3[0],
      setIsFullScreen = _useState3[1];

  var disableFullscreen = useMemo(function () {
    var isEPG = !!watch.episode.source_epg_id;

    if (maybeIOS(deviceInfo) && liveStatus(watch.episode) && (isEPG || !watch.episode.start_over_flag)) {
      return true;
    } else {
      return false;
    }
  }, [deviceInfo, watch.episode]);
  var disablePause = useMemo(function () {
    if (liveStatus(watch.episode) && !watch.episode.start_over_flag) {
      return true;
    } else {
      return false;
    }
  }, [watch.episode]);
  var disablePictureInPicture = useMemo(function () {
    return (deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.isIPad) || (deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.isIPhone) || false;
  }, [deviceInfo]);
  var autoPlay = autoPlayEnabled(deviceInfo, disablePause);
  var env = process.env.APP_ENV || "production" || "development";
  var debug = env !== "production";
  var handlePlayerReady = useCallback(function () {
    playerRef.current.on("error", function () {
      // player error handling
      var err = playerRef.current.error();
      var code = err.code;
      var npawMessage = MEDIA_ERROR_MESSAGES[code] || PLAYER_ERROR_MESSAGES[code] || err.message || WATCH_ERROR_MESSAGES[0]; // 連携されないNPAWエラーを送る処理追加

      window.plugin.fireError(err.code, npawMessage);
      setPlayerError(new StreaksPlayerError(err.message, err.code));
    });

    if (onReady) {
      onReady(playerRef.current);
    }
  }, []);
  var loadMedia = useCallback(function () {
    var canplay = watchState.canplay,
        mediaSource = watchState.mediaSource,
        resumePoint = watchState.resumePoint;

    if (!mediaLoaded && canplay && mediaSource) {
      var _watch$uid2Token$toke, _watch$uid2Token;

      // test
      // const url =
      //   "https://multiplatform-f.akamaihd.net/i/multi/april11/sintel/sintel-hd_,512x288_450_b,640x360_700_b,768x432_1000_b,1024x576_1400_m,.mp4.csmil/master.m3u8"; // debug
      // player.src([{ src: url, type: "application/x-mpegURL" }]);
      var qualityLevels = function () {
        var levels = function () {
          if (watch.episode.plalaFlag) {
            return plalaQualityLevels;
          } else {
            return streaksQualityLevels;
          }
        }(); // mediaSource.sourcesのresolutionをみて最大画質を決める


        var source = mediaSource.sources.find(function (source) {
          if (deviceInfo !== null && deviceInfo !== void 0 && deviceInfo.isSafari) {
            return !!source.key_systems["com.apple.fps.1_0"];
          } else {
            return !source.key_systems["com.apple.fps.1_0"];
          }
        });
        var height = source.resolution.split("x")[1];
        var filterdLevels = levels.filter(function (level) {
          return level.maximumHeight <= height;
        });
        return [].concat(_toConsumableArray(filterdLevels), [{
          label: "自動",
          selected: true
        }]);
      }();

      var loadMediaOption = {
        customQualityLevels: qualityLevels
      };

      if (resumePoint !== null && resumePoint >= 0) {
        loadMediaOption.startTime = resumePoint;
      }

      var uid2Token = (_watch$uid2Token$toke = watch === null || watch === void 0 ? void 0 : (_watch$uid2Token = watch.uid2Token) === null || _watch$uid2Token === void 0 ? void 0 : _watch$uid2Token.token) !== null && _watch$uid2Token$toke !== void 0 ? _watch$uid2Token$toke : "null";
      var uid2Params = encodeURIComponent("uid2_token=".concat(uid2Token));
      loadMediaOption.advertising = {
        adsParams: {
          cust_params: uid2Params
        }
      }; // ぷらら動画に対応で、FairPlayの場合はContent-Typeを指定する必要がある

      mediaSource.sources.forEach(function (source) {
        var _source$key_systems, _fairplayKeySystem$he;

        var fairplayKeySystem = source === null || source === void 0 ? void 0 : (_source$key_systems = source.key_systems) === null || _source$key_systems === void 0 ? void 0 : _source$key_systems["com.apple.fps.1_0"];

        if (fairplayKeySystem && fairplayKeySystem !== null && fairplayKeySystem !== void 0 && fairplayKeySystem.headers && fairplayKeySystem !== null && fairplayKeySystem !== void 0 && (_fairplayKeySystem$he = fairplayKeySystem.headers) !== null && _fairplayKeySystem$he !== void 0 && _fairplayKeySystem$he["acquirelicenseassertion"]) {
          fairplayKeySystem.headers["Content-Type"] = "application/x-www-form-urlencoded";
        }
      });
      playerRef.current.loadMedia(mediaSource, loadMediaOption, function () {
        attachNPAWPlugin(playerRef.current, user, watch, debug);
      });
      playerRef.current.play();
      setMediaLoaded(true);
    }
  }, [watch, user, watchState, mediaLoaded, debug]);
  var handlePlay = useCallback(function () {
    if (watchState.reason === "entitlement_reconfirm") {
      return watchState.displayOverlay({
        reason: "entitlement_confirm"
      });
    }

    if (!mediaLoaded) {
      loadMedia();
    }
  }, [watchState, mediaLoaded]);
  useEffect(function () {
    if (watch.episode && !playerRef.current) {
      var video = document.createElement("video");
      video.setAttribute("poster", poster);
      video.setAttribute("controls", true);
      document.querySelector(".".concat(styles.videoWrapper)).append(video);
      var options = {
        language: "ja",
        chromeless: false,
        timeShift: watch.episode.is_live && watch.episode.start_over_flag,
        pictureInPicture: true,
        // preload: "metadata",
        crossorigin: "anonymous",
        autoplay: autoPlay,
        advertising: {
          debug: debug,
          requestMode: "ondemand",
          repeat: false
        }
      };
      playerRef.current = streaksplayer(video, _objectSpread(_objectSpread({}, options), playerOption), handlePlayerReady);
    }
  }, [watch, autoPlay, debug]);
  useEffect(function () {
    debugLog({
      autoPlay: autoPlay
    });
    var canplay = watchState.canplay,
        mediaSource = watchState.mediaSource;

    if (canplay && mediaSource && autoPlay) {
      loadMedia();
    } // next_episode以外のダイアログが表示された場合に全画面を解除する？

  }, [watchState, autoPlay]);
  useEffect(function () {
    // PiP中にページ遷移するとPiPのウインドウが残ってしまうので要素が残っていたらexitPictureInPictureを実行する
    return function () {
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture();
      }
    };
  }, []); // fullscreenchangeイベントの監視と状態の更新

  useEffect(function () {
    if (playerRef.current) {
      playerRef.current.on("fullscreenchange", function () {
        setIsFullScreen(playerRef.current.isFullscreen());
      });
    }
  }, [playerRef.current]); // playerの子要素としてFullscreenOverlayを追加

  useEffect(function () {
    if (overlayRef.current && playerRef.current) {
      playerRef.current.el().appendChild(overlayRef.current);
    }

    return function () {
      if (overlayRef.current && playerRef.current) {
        if (playerRef.current.el().contains(overlayRef.current)) {
          playerRef.current.el().removeChild(overlayRef.current);
        }
      }
    };
  }, [overlayRef.current, playerRef.current]); // fullscreenの状態に応じてFullscreenOverlayの表示・非表示を切り替える

  useEffect(function () {
    if (overlayRef.current) {
      if (isFullScreen) {
        overlayRef.current.style.visibility = "visible";
      } else {
        overlayRef.current.style.visibility = "hidden";
      }
    }
  }, [isFullScreen, overlayRef.current]);
  useEffect(function () {
    var handler = function handler(url) {
      setMediaLoaded(false);
      playerRef.current.pause();
      overlayRef.current = null; //プレイヤー以外の画面に遷移時にプレイヤー破棄

      if (url && !url.includes("/watch")) {
        playerRef.current.dispose();
      }
    };

    router.events.on("routeChangeStart", handler);
    return function () {
      router.events.off("routeChangeStart", handler);
    };
  }, []);
  var channelId = Number(watch === null || watch === void 0 ? void 0 : (_watch$episode = watch.episode) === null || _watch$episode === void 0 ? void 0 : (_watch$episode$channe = _watch$episode.channel) === null || _watch$episode$channe === void 0 ? void 0 : _watch$episode$channe.id);
  var showValuePlanLabel = useMemo(function () {
    return isShowValuePlanLabel({
      channelId: channelId
    });
  }, [channelId]);
  return /*#__PURE__*/_jsx("div", {
    className: styles.container,
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.inner,
      children: [showValuePlanLabel && !mediaLoaded && !playerRef.current && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.valueplanLabel,
          children: /*#__PURE__*/_jsx(Image, {
            src: "/images/valueplan_label.png",
            alt: "valueplan_label",
            layout: "fill",
            objectFit: "cover"
          })
        }), /*#__PURE__*/_jsx("div", {
          className: styles.leminoLogo,
          children: /*#__PURE__*/_jsx(Image, {
            src: "/images/lemino_logo.png",
            alt: "lemino_logo",
            layout: "fill",
            objectFit: "contain"
          })
        })]
      }), /*#__PURE__*/_jsx(PlayerSurface, {
        active: watchState.canplay && !mediaLoaded || watchState.overlay,
        onClick: handlePlay,
        watch: watch
      }), !isFullScreen && /*#__PURE__*/_jsx(VideoOverlay, {
        watch: watch,
        state: watchState,
        entitlements: entitlements,
        error: playerError,
        poster: poster
      }), /*#__PURE__*/_jsx("div", {
        className: classnames(styles.videoWrapper, (_classnames = {}, _defineProperty(_classnames, styles.disableFullscreen, disableFullscreen), _defineProperty(_classnames, styles.disablePause, disablePause), _defineProperty(_classnames, styles.disablePictureInPicture, disablePictureInPicture), _classnames))
      }), /*#__PURE__*/_jsx("div", {
        ref: overlayRef,
        children: /*#__PURE__*/_jsx(FullScreenOverlay, {
          ref: overlayRef,
          watch: watch,
          state: watchState,
          error: playerError,
          fullScreen: isFullScreen,
          player: playerRef.current
        })
      })]
    })
  });
}

export default StreaksPlayer;